/**
 * @fileoverview User Management KPI strip.
 * @description The four KPI tiles above the table (total users, Sales Heads, Managers, Executives),
 * from GET /users/summary, each with its active and inactive counts. They ignore the table's
 * filters. A tile shows a dash until the summary has loaded.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import KpiStrip from '@/shared/components/molecules/KpiStrip'

import type { UsersSummary } from '@/features/users/types/users.types'

interface UserKpiStripProps {
  /** The summary, once loaded. */
  summary: UsersSummary | undefined
}

/**
 * @component
 * @param {UserKpiStripProps} props - The summary, once loaded.
 * @param {UsersSummary | undefined} props.summary - The directory's summary.
 */
function UserKpiStrip({ summary }: UserKpiStripProps) {
  const { t } = useTranslation()
  const counts = summary?.statusCounts

  /**
   * @description Builds a KPI tile's "n active · n inactive" line.
   * @param {{ active: number; inactive: number } | undefined} count - The tile's counts, once
   * loaded.
   * @returns {string | undefined} The line, or nothing until the summary has loaded.
   */
  const statusLine = (count: { active: number; inactive: number } | undefined) =>
    count ? t('sales.userManagement.kpi.statusLine', count) : undefined

  const tiles = [
    {
      key: 'total',
      value: summary?.totalUsers,
      counts: counts?.all,
      icon: 'users',
      iconTone: 'navy',
    },
    {
      key: 'heads',
      value: summary?.salesHeads,
      counts: counts?.salesHeads,
      icon: 'shield',
      iconTone: 'blue',
    },
    {
      key: 'managers',
      value: summary?.salesManagers,
      counts: counts?.salesManagers,
      icon: 'briefcase',
      iconTone: 'purple',
    },
    {
      key: 'executives',
      value: summary?.salesExecutives,
      counts: counts?.salesExecutives,
      icon: 'userCheck',
      iconTone: 'amber',
    },
  ] as const

  return (
    <KpiStrip
      tiles={tiles.map(({ key, value, counts: tileCounts, icon, iconTone }) => ({
        label: t(`sales.userManagement.kpi.${key}`),
        // The status counts fill the sublabel once loaded; until then the tile's caption shows.
        sublabel: statusLine(tileCounts) ?? t(`sales.userManagement.kpi.${key}Sublabel`),
        icon,
        iconTone,
        value: value ?? '—',
        testId: `user-management-kpi-${key}`,
      }))}
    />
  )
}

export default UserKpiStrip
